// ProposalCard — a proposed change to a material, as a card in the thread (pen
// "Chat – every entry" §6, "Proposal · waiting for you"). Pure.
//
// The card is for a proposal that waits for you (once settled it is one line
// in the trail, see ChatMessage). What changes (op-count chips: added /
// changed / removed), where (the material), why (the agent's one line) and
// how well it is backed, then the actions. Review is the main action: it opens
// the material at the change. Accept is the shortcut once you trust it;
// Discard is quiet, at the far end (pen decision 6). Accept / Discard show
// only when wired.
//
// `ProposalSummaryText` (the op counts as plain words) is shared with the
// compact one-line chip.

import { exhaustSwitchCase } from "@bilda/utils";
import { Trans, useLingui } from "@lingui/react/macro";
import type { ReactNode } from "react";
import { Button } from "../../Button/Button.js";
import type { GroundingState } from "../../Grounding/types.js";
import { Icon } from "../../Icon/Icon.js";
import type { ProposalOpCount, ProposalSummary } from "../types.js";
import "./ProposalCard.css";

export type ProposalCardProps = {
    readonly summary: ProposalSummary;
    readonly rationale: string;
    readonly grounding: GroundingState;
    // The material the change targets, when known.
    readonly materialTitle?: string;
    // Review: open the material at the change.
    readonly onGoToChange: () => void;
    // Secondary pointer (optional): review the change in place without leaving.
    readonly onReviewInPlace?: () => void;
    readonly onAccept?: () => void;
    readonly onDiscard?: () => void;
    readonly className?: string;
};

// One op count in words. A new op kind fails to compile here until its words
// are written.
const SummaryPart = ({ part }: { part: ProposalOpCount }): ReactNode => {
    switch (part.type) {
        case "added":
            return <Trans>{part.count} added</Trans>;
        case "removed":
            return <Trans>{part.count} removed</Trans>;
        case "moved":
            return <Trans>{part.count} moved</Trans>;
        case "edited":
            return part.target === undefined ? (
                <Trans>{part.count} edited</Trans>
            ) : (
                <Trans>
                    {part.count} edited to {part.target}
                </Trans>
            );
        default:
            return exhaustSwitchCase(part);
    }
};

// "1 added · 1 edited to X" — the compact chip's phrase. The middot is drawn
// by CSS between parts.
export const ProposalSummaryText = ({
    summary,
}: {
    summary: ProposalSummary;
}): ReactNode =>
    summary.map((part) => (
        <span key={part.type} className="bilda-proposal-card__part">
            <SummaryPart part={part} />
        </span>
    ));

// One op count as pen's chip: added green, removed red, the rest neutral.
const OpChip = ({ part }: { part: ProposalOpCount }): ReactNode => {
    switch (part.type) {
        case "added":
            return (
                <span className="bilda-proposal-card__op bilda-proposal-card__op--added">
                    <Trans>+ {part.count} added</Trans>
                </span>
            );
        case "removed":
            return (
                <span className="bilda-proposal-card__op bilda-proposal-card__op--removed">
                    <Trans>− {part.count} removed</Trans>
                </span>
            );
        case "edited":
            return (
                <span className="bilda-proposal-card__op">
                    <Trans>{part.count} changed</Trans>
                </span>
            );
        case "moved":
            return (
                <span className="bilda-proposal-card__op">
                    <Trans>{part.count} moved</Trans>
                </span>
            );
        default:
            return exhaustSwitchCase(part);
    }
};

// How well the change is backed (pen "Förankring"): backed is quiet (no
// fill); partly and not backed are tinted and say so. Nothing when backing
// does not apply.
const Backing = ({ grounding }: { grounding: GroundingState }): ReactNode => {
    switch (grounding.type) {
        case "grounded":
            return (
                <span className="bilda-proposal-card__backing bilda-proposal-card__backing--backed">
                    <Icon name="shield" />
                    <Trans>Backed by your sources</Trans>
                </span>
            );
        case "weak":
            return (
                <span className="bilda-proposal-card__backing bilda-proposal-card__backing--partly">
                    <Icon name="shield-alert" />
                    <Trans>Partly backed by your sources</Trans>
                </span>
            );
        case "ungrounded":
            return (
                <span className="bilda-proposal-card__backing bilda-proposal-card__backing--none">
                    <Icon name="shield-off" />
                    <Trans>Not backed by your sources</Trans>
                </span>
            );
        case "unsupported":
            return null;
        default:
            return exhaustSwitchCase(grounding);
    }
};

export const ProposalCard = ({
    summary,
    rationale,
    grounding,
    materialTitle,
    onGoToChange,
    onReviewInPlace,
    onAccept,
    onDiscard,
    className,
}: ProposalCardProps) => {
    const { t } = useLingui();
    return (
        // biome-ignore lint/a11y/useSemanticElements: group (not region): many cards share this name, and same-named landmarks violate landmark-unique
        <section
            role="group"
            className={`bilda-proposal-card${className ? ` ${className}` : ""}`}
            aria-label={t`Proposed change`}
        >
            <div className="bilda-proposal-card__kicker">
                <Icon
                    name="git-pull-request-arrow"
                    className="bilda-proposal-card__kicker-icon"
                />
                <span className="bilda-proposal-card__kicker-label">
                    <Trans>Proposal</Trans>
                </span>
                <span className="bilda-proposal-card__waiting">
                    <Trans>Waiting for you</Trans>
                </span>
            </div>
            {materialTitle === undefined ? null : (
                <p className="bilda-proposal-card__title">{materialTitle}</p>
            )}
            <div className="bilda-proposal-card__ops">
                {summary.map((part) => (
                    <OpChip key={part.type} part={part} />
                ))}
            </div>
            {rationale === "" ? null : (
                <p className="bilda-proposal-card__why">{rationale}</p>
            )}
            <Backing grounding={grounding} />
            <div className="bilda-proposal-card__actions">
                <Button variant="primary" size="md" onClick={onGoToChange}>
                    <Trans context="action">Review</Trans>
                </Button>
                {onReviewInPlace ? (
                    <Button variant="ghost" size="md" onClick={onReviewInPlace}>
                        <Trans>Review in place</Trans>
                    </Button>
                ) : null}
                {onAccept ? (
                    <Button variant="secondary" size="md" onClick={onAccept}>
                        <Trans>Accept</Trans>
                    </Button>
                ) : null}
                {onDiscard ? (
                    <Button
                        variant="ghost"
                        size="md"
                        onClick={onDiscard}
                        className="bilda-proposal-card__discard"
                    >
                        <Trans>Discard</Trans>
                    </Button>
                ) : null}
            </div>
        </section>
    );
};
